import { PromotionSkipCount, SkipReasonCount } from '../../graphql/promotions'
import { skipReasonLabel } from '../../helpers/skipReasonLabels'
import { isUnattributedSkip, UNATTRIBUTED_LABEL } from './unattributedSkip'

/**
 * Chart data for the skip screen, as pure functions.
 *
 * Not a style preference. Recharts draws into an SVG sized by ResponsiveContainer, which
 * measures its parent, and every parent is 0x0 under jsdom — so nothing inside a chart is ever
 * in the DOM during a test. On the Overview, a test written against the rendered chart
 * asserted a currency invariant and stayed green when the filter enforcing it was deleted.
 * The invariants live here so they can actually fail.
 *
 * EVERYTHING HERE IS A COUNT, and that is why this file carries no currency rule. Counts are
 * dimensionless — `formatCount` says so — so unlike the Overview's money charts there is
 * nothing that must be split per unit. The one rule that does apply is the screen's own.
 */

/**
 * Charting every row produces an unreadable comb; the tables are the complete view.
 *
 * Defined here rather than imported from the Overview's chartSeries: it is a tuning value for
 * this screen's density, not a shared contract, and importing it would couple two screens
 * through a number either might want to change alone.
 */
export const MAX_SKIP_BARS = 8

export interface SkipBar {
  /** The translated sentence, because the chart axis is read directly. */
  label: string
  count: number
  /** Distinguishes the withheld bucket for styling and for tests. */
  unattributed: boolean
}

/** Reasons, largest first. Already one row per reason — no aggregation needed. */
export function reasonBars(byReason: SkipReasonCount[]): SkipBar[] {
  return [...byReason]
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_SKIP_BARS)
    .map((row) => ({
      label: skipReasonLabel(row.reason),
      count: row.count,
      unattributed: false
    }))
}

/**
 * Promotions, summed across their reasons.
 *
 * THE WITHHELD BUCKET IS AGGREGATED AND KEPT, never dropped. Rows whose promotion this
 * operator cannot see arrive blank-identified rather than omitted, precisely so this list's
 * difference from the by-reason totals is not the exact number withheld. A chart that
 * silently excluded them would restore that leak at the last moment — and a chart is the
 * easiest place to lose a row, because nobody counts bars.
 *
 * They collapse into ONE bar rather than one per reason: the operator cannot act on them
 * individually, and several identical unlabelled bars read as a rendering fault.
 */
export function promotionBars(rows: PromotionSkipCount[]): SkipBar[] {
  const totals = new Map<string, SkipBar>()

  rows.forEach((row) => {
    const unattributed = isUnattributedSkip(row)
    // Keyed on the id, so every withheld row lands in one bucket regardless of its reason.
    const key = unattributed ? '' : row.promotionId
    const existing = totals.get(key)
    if (existing) {
      existing.count += row.count
      return
    }
    totals.set(key, {
      label: unattributed ? UNATTRIBUTED_LABEL : row.name,
      count: row.count,
      unattributed
    })
  })

  return Array.from(totals.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_SKIP_BARS)
}

/** An all-zero chart draws axes and no data; the summary line already reported the zero. */
export function worthCharting(bars: SkipBar[]): boolean {
  return bars.some((bar) => Number.isFinite(bar.count) && bar.count > 0)
}
